/**
 * "Who can edit this app" (FRONT-2937, ADR-0081 decision 1): the owner adds
 * colleagues as editors, who may then change the app's code in their own
 * draft, and removes them again. Shown to the owner only, while
 * `eddy.app-drafts` is on, and never on an app with no recorded owner — the
 * same three facts the server's editor routes check.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CircleNotch, UserCircleIcon, XIcon } from '@phosphor-icons/react';
import { api } from '~/lib/api-client';
import { appDraftKeys, appEditorKeys } from '~/lib/query-keys';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Segmented } from '~/components/ui/segmented';
import { usePeopleSearch, type PersonSuggestion } from '~/hooks/queries/use-people-search';
import { MIN_PEOPLE_QUERY_LENGTH } from './share-people-input';

/** Whether an editor sees the app's data. */
export type EditorLevel = 'code' | 'code_and_data';

export interface AppEditor {
  userId: string;
  name: string | null;
  email: string | null;
  /** Optional so a cached answer from before levels existed reads as the safe default. */
  level?: EditorLevel;
}

export const EDITOR_LEVEL_OPTIONS: { value: EditorLevel; label: string }[] = [
  { value: 'code', label: 'Code only' },
  { value: 'code_and_data', label: 'Code and data' },
];

/** What the section says under its heading. */
export const EDITORS_EXPLANATION =
  "Editors change the app in their own draft. Their changes reach the app when you accept them. Code only editors change the code and never see the app's data; Code and data editors also see its data and can use the app's key, which can act as you within the tools and agents the app is allowed.";

/** A downgrade or removal cuts off every door that checks the person, but not a copy of the app's key they hold. */
export function keyCopyWarning(name: string): string {
  return `${name} could still use a copy of the app's key. Replace the key to cut them off.`;
}

/** Pure: who may still hold a copy of the key after this change to `editor` — only a Code and data editor going to Code only, or removed. */
export function keyHolderOf(editor: AppEditor, next: EditorLevel | 'removed'): string | null {
  if (editor.level !== 'code_and_data' || next === 'code_and_data') return null;
  return label(editor);
}

interface KeyChange {
  isSuccess: boolean;
  submittedAt: number;
  variables?: { holder?: string | null };
}

/** Pure: the name to warn about — the latest successful change that left a copy behind, unless the key was replaced since. */
export function keyCopyHolder(changes: KeyChange[], replacedAt: number): string | null {
  const latest = changes
    .filter((c) => c.isSuccess && c.variables?.holder && c.submittedAt > replacedAt)
    .sort((a, b) => b.submittedAt - a.submittedAt)[0];
  return latest?.variables?.holder ?? null;
}

/** The warning the box shows once the app has an editor. */
export const EDITORS_CODE_WARNING = "Editors can read all of this app's code and its history. Never put data or keys in the code.";

interface EditorsAnswer {
  editors: AppEditor[];
  notice?: string | null;
}

const SEARCH_DEBOUNCE_MS = 200;

/** Pure: whether this viewer gets the section — the owner of an app with a recorded owner, with the flag on. */
export function showsEditorsSection(app: { isOwner: boolean; ownerUserId: string | null }, draftsOn: boolean): boolean {
  return draftsOn && app.isOwner && app.ownerUserId != null;
}

/** Pure: what the owner is told after an add — the server's first-editor notice, else a confirmation. */
export function addedEditorMessage(answer: { notice?: string | null }, who: string): string {
  return answer.notice ?? `${who} can now edit this app.`;
}

const label = (e: { name: string | null; email: string | null; userId: string }) => e.name ?? e.email ?? e.userId;

/** Pure: who can still be added — never someone already an editor, nor the owner, who always edits. */
export function editorSuggestions(people: PersonSuggestion[], editors: AppEditor[], ownerUserId: string): PersonSuggestion[] {
  const taken = new Set([ownerUserId, ...editors.map((e) => e.userId)]);
  return people.filter((p) => !taken.has(p.userId));
}

/** Pure: who the app can be handed to — anyone in the workspace, its editors included, but its owner. */
export function transferSuggestions(people: PersonSuggestion[], ownerUserId: string): PersonSuggestion[] {
  return people.filter((p) => p.userId !== ownerUserId);
}

export const TRANSFER_PENDING = 'Transferring…';
export const TRANSFER_PICK_FIRST = 'Choose who should own this app first.';

/** Pure: why Transfer cannot be pressed right now; null when it can. One decision for disabled and its explanation. */
export function transferGate(input: { pending: boolean; picked: boolean }): string | null {
  if (input.pending) return TRANSFER_PENDING;
  if (!input.picked) return TRANSFER_PICK_FIRST;
  return null;
}

/** Pure: what the old owner is told once the app is someone else's. */
export function transferredMessage(who: string): string {
  return `${who} now owns this app. You can still change it as an editor.`;
}

/** Pure: what a transfer to `who` does, said before it is made. */
export function transferConsequence(who: string): string {
  return `${who} will own this app. You will become a Code only editor and keep your draft: you won't see the app's data until ${who} decides to share it with you. You will no longer be able to change who can open it or who edits it. The app's key is replaced, so any script using the old key stops working.`;
}

interface TransferAnswer {
  owner: { userId: string; name: string | null };
  editors: AppEditor[];
}

/** The transfer's own form: the picker, what will happen, and the button that does it. */
export function TransferForm(props: {
  query: string;
  onQuery: (q: string) => void;
  suggestions: PersonSuggestion[];
  picked: PersonSuggestion | null;
  onPick: (p: PersonSuggestion) => void;
  pending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const who = props.picked ? (props.picked.name ?? props.picked.email) : null;
  const blockedBy = transferGate({ pending: props.pending, picked: props.picked !== null });
  return (
    <div className="space-y-2 rounded-md border border-border p-3">
      <Input
        value={props.query}
        onChange={(ev) => props.onQuery(ev.target.value)}
        placeholder="Search for the new owner by name or email"
        aria-label="Search for the new owner by name or email"
        disabled={props.pending}
      />
      {props.suggestions.length > 0 && (
        <ul className="rounded-md border border-border bg-popover">
          {props.suggestions.map((p) => (
            <li key={p.userId}>
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-muted"
                onClick={() => props.onPick(p)}
              >
                <span className="truncate">{p.name ?? p.email}</span>
                {p.name && <span className="truncate text-xs text-muted-foreground">{p.email}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
      {who ? (
        <p className="text-xs text-muted-foreground">
          {transferConsequence(who)}
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">{blockedBy}</p>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={props.onCancel} disabled={props.pending}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="destructive"
          size="sm"
          data-testid="transfer-confirm"
          disabled={blockedBy !== null}
          tooltip={blockedBy ?? undefined}
          onClick={props.onConfirm}
        >
          {props.pending && <CircleNotch size={14} className="animate-spin" />}
          {props.pending ? TRANSFER_PENDING : who ? `Transfer to ${who}` : 'Transfer'}
        </Button>
      </div>
    </div>
  );
}

/** "Transfer ownership" under the editors list: hands the app to someone else, who becomes its owner. `defaultOpen` shows the form at once. */
export function TransferOwnership({ slug, ownerUserId, defaultOpen = false }: { slug: string; ownerUserId: string; defaultOpen?: boolean }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(defaultOpen);
  const [picked, setPicked] = useState<PersonSuggestion | null>(null);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);
  const search = usePeopleSearch(debounced, open && debounced.length >= MIN_PEOPLE_QUERY_LENGTH);
  const suggestions = !picked && debounced === query.trim() ? transferSuggestions(search.people, ownerUserId) : [];

  const transfer = useMutation({
    mutationFn: (person: { userId: string; who: string }) =>
      api.post<TransferAnswer>(`/api/admin/apps/${slug}/transfer`, { userId: person.userId }),
    onSuccess: (_answer, person) => {
      // The page re-renders as the editor's: the app, its place in the list, its editors and its drafts all changed.
      qc.invalidateQueries({ queryKey: ['admin-app', slug] });
      qc.invalidateQueries({ queryKey: ['admin-apps'] });
      qc.invalidateQueries({ queryKey: appEditorKeys.list(slug) });
      qc.invalidateQueries({ queryKey: appDraftKeys.list(slug) });
      setOpen(false);
      toast.success(transferredMessage(person.who));
    },
    onError: (e: Error) => toast.error(e.message || 'Could not transfer the app'),
  });

  const close = () => {
    setOpen(false);
    setPicked(null);
    setQuery('');
  };

  if (!open) {
    return (
      <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(true)}>
        Transfer ownership
      </Button>
    );
  }
  return (
    <TransferForm
      query={picked ? (picked.name ?? picked.email) : query}
      onQuery={(q) => {
        setPicked(null);
        setQuery(q);
      }}
      suggestions={suggestions}
      picked={picked}
      onPick={setPicked}
      pending={transfer.isPending}
      onConfirm={() => picked && transfer.mutate({ userId: picked.userId, who: picked.name ?? picked.email })}
      onCancel={close}
    />
  );
}

export function EditorsSection({ slug, ownerUserId }: { slug: string; ownerUserId: string }) {
  const qc = useQueryClient();
  const key = appEditorKeys.list(slug);
  const { data, isLoading, isError } = useQuery({
    queryKey: key,
    queryFn: () => api.get<EditorsAnswer>(`/api/admin/apps/${slug}/editors`),
  });
  const editors = data?.editors ?? [];

  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);
  const search = usePeopleSearch(debounced, debounced.length >= MIN_PEOPLE_QUERY_LENGTH);
  const suggestions = debounced === query.trim() ? editorSuggestions(search.people, editors, ownerUserId) : [];

  const add = useMutation({
    mutationFn: (person: { userId: string; who: string }) =>
      api.post<EditorsAnswer>(`/api/admin/apps/${slug}/editors`, { userId: person.userId }),
    onSuccess: (answer, person) => {
      qc.setQueryData(key, { editors: answer.editors });
      // FRONT-2940: their draft reads active again on the Drafts list.
      qc.invalidateQueries({ queryKey: appDraftKeys.list(slug) });
      setQuery('');
      toast.success(addedEditorMessage(answer, person.who));
    },
    onError: (e: Error) => toast.error(e.message || 'Could not add the editor'),
  });
  const remove = useMutation({
    mutationFn: ({ userId }: { userId: string; holder: string | null }) =>
      api.delete<EditorsAnswer>(`/api/admin/apps/${slug}/editors/${encodeURIComponent(userId)}`),
    onSuccess: (answer) => {
      qc.setQueryData(key, { editors: answer.editors });
      // FRONT-2940: their draft turns inactive on the Drafts list, with its Delete.
      qc.invalidateQueries({ queryKey: appDraftKeys.list(slug) });
    },
    onError: (e: Error) => toast.error(e.message || 'Could not remove the editor'),
  });
  const setLevel = useMutation({
    mutationFn: (change: { userId: string; level: EditorLevel; holder: string | null }) =>
      api.put<EditorsAnswer>(`/api/admin/apps/${slug}/editors/${encodeURIComponent(change.userId)}/level`, { level: change.level }),
    onSuccess: (answer) => qc.setQueryData(key, { editors: answer.editors }),
    onError: (e: Error) => toast.error(e.message || "Could not change the editor's access"),
  });
  // Carries the key's current scopes: a bare rotate falls back to the read-only defaults.
  const replaceKey = useMutation({
    mutationFn: async () => {
      const { token } = await api.get<{ token: { scopes: string[] } | null }>(`/api/admin/apps/${slug}/token`);
      if (!token) return { replaced: false };
      await api.post(`/api/admin/apps/${slug}/token`, { rotate: true, scopes: token.scopes });
      return { replaced: true };
    },
    onSuccess: (out) => {
      qc.invalidateQueries({ queryKey: ['admin-app-token', slug] });
      toast.success(out.replaced ? "The app's key is replaced." : "The app has no key, so there was nothing to replace.");
    },
    onError: (e: Error) => toast.error(e.message || "Could not replace the app's key"),
  });
  const keyHolder = keyCopyHolder([setLevel, remove], replaceKey.isSuccess ? replaceKey.submittedAt : 0);

  return (
    <div className="rounded-md border border-border bg-card/40 p-4 space-y-3">
      <div>
        <p className="text-sm font-medium">Who can edit this app</p>
        <p className="text-xs text-muted-foreground">{EDITORS_EXPLANATION}</p>
      </div>
      {editors.length > 0 && <p className="text-xs text-foreground">{EDITORS_CODE_WARNING}</p>}
      {keyHolder && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2">
          <p className="text-xs text-foreground">{keyCopyWarning(keyHolder)}</p>
          <Button type="button" variant="outline" size="sm" disabled={replaceKey.isPending} onClick={() => replaceKey.mutate()}>
            Replace the key
          </Button>
        </div>
      )}
      {isError ? (
        <p className="text-xs text-destructive">Could not load this app&rsquo;s editors.</p>
      ) : isLoading ? null : editors.length === 0 ? (
        <p className="text-xs text-muted-foreground">Only you can change this app.</p>
      ) : (
        <ul className="space-y-1.5">
          {editors.map((e) => (
            <li key={e.userId} className="flex flex-wrap items-center justify-between gap-2 text-sm">
              <span className="flex min-w-0 items-center gap-2">
                <UserCircleIcon size={16} className="shrink-0 text-muted-foreground" />
                <span className="truncate">{label(e)}</span>
                {e.name && e.email && <span className="truncate text-xs text-muted-foreground">{e.email}</span>}
              </span>
              <span className="flex shrink-0 items-center gap-1">
                <Segmented
                  size="sm"
                  aria-label={`What ${label(e)} can see`}
                  value={e.level ?? 'code'}
                  onChange={(level) => setLevel.mutate({ userId: e.userId, level, holder: keyHolderOf(e, level) })}
                  options={EDITOR_LEVEL_OPTIONS.map((o) => ({ ...o, disabled: setLevel.isPending }))}
                />
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Remove ${label(e)} as an editor`}
                  tooltip="Remove as editor"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate({ userId: e.userId, holder: keyHolderOf(e, 'removed') })}
                >
                  <XIcon size={14} />
                </Button>
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="space-y-1.5">
        <Input
          value={query}
          onChange={(ev) => setQuery(ev.target.value)}
          placeholder="Add an editor by name or email"
          aria-label="Add an editor by name or email"
          disabled={add.isPending}
        />
        {suggestions.length > 0 && (
          <ul className="rounded-md border border-border bg-popover">
            {suggestions.map((p) => (
              <li key={p.userId}>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-muted"
                  onClick={() => add.mutate({ userId: p.userId, who: p.name ?? p.email })}
                >
                  <span className="truncate">{p.name ?? p.email}</span>
                  {p.name && <span className="truncate text-xs text-muted-foreground">{p.email}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <TransferOwnership slug={slug} ownerUserId={ownerUserId} />
    </div>
  );
}
